// ============================================================================
// FACILITATOR CHAT PANEL
// Embedded chat panel for facilitator guidance (renders inside Guidance tab)
// ============================================================================

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MessageSquare } from 'lucide-react';
import { ChatMessage } from '../../types';
import { getChatHistory } from '../../services/facilitator-chat';
import {
  sendChatMessageViaN8n,
  executeQuickActionViaN8n
} from '../../services/n8n';
import { ChatMessageComponent } from './ChatMessage';
import { QuickActionButtons } from './QuickActionButtons';

interface FacilitatorChatPanelProps {
  sessionId: string;
  currentPhase: any;
  transcript: any[];
  guidance: any | null;
  requirements: any | null;
  sessionAvatars: any[];
}

export function FacilitatorChatPanel({
  sessionId,
  currentPhase,
  transcript,
  guidance,
  requirements,
  sessionAvatars,
}: FacilitatorChatPanelProps) {
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [inputText, setInputText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadChatHistory = useCallback(async () => {
    try {
      const history = await getChatHistory(sessionId);
      setChatMessages(history);
    } catch (err) {
      console.error('Failed to load chat history:', err);
      setError('Failed to load chat history');
    }
  }, [sessionId]);

  // Load chat history on mount
  useEffect(() => {
    if (sessionId) {
      loadChatHistory();
    }
  }, [sessionId, loadChatHistory]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    scrollToBottom();
  }, [chatMessages]);

  const handleSendMessage = async () => {
    if (!inputText.trim() || isLoading) return;

    const userMessage = inputText;
    setInputText('');
    setIsLoading(true);
    setError(null);

    try {
      await sendChatMessageViaN8n(sessionId, userMessage);
      await loadChatHistory();
    } catch (err: any) {
      console.error('Failed to send message:', err);
      setError(err.message || 'Failed to send message');
      setInputText(userMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleQuickAction = async (actionId: string) => {
    if (isLoading) return;

    setIsLoading(true);
    setError(null);

    try {
      await executeQuickActionViaN8n(sessionId, actionId);
      await loadChatHistory();
    } catch (err: any) {
      console.error('Failed to execute quick action:', err);
      setError(err.message || 'Failed to execute quick action');
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Header */}
      <div className="px-5 py-4 border-b border-slate-200 shrink-0">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-primary">
            <MessageSquare size={16} />
          </div>
          <div>
            <h3 className="text-base font-bold text-navy">Facilitator Assistant</h3>
            <p className="text-slate-500 text-xs">AI guidance for your session</p>
          </div>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 text-red-600 px-4 py-2 text-sm border-b border-red-200 shrink-0">
          ⚠️ {error}
        </div>
      )}

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto px-4 py-3 flex flex-col gap-3 min-h-0">
        {chatMessages.length === 0 ? (
          <div className="text-center text-slate-500 text-sm mt-8">
            <div className="text-3xl mb-3">💡</div>
            <p>Ask me anything about your session!</p>
            <p className="text-xs mt-1 text-slate-400">
              I can help with balance, guidance,<br />requirements, and progress.
            </p>
          </div>
        ) : (
          chatMessages.map((msg) => (
            <ChatMessageComponent key={msg.id} message={msg} />
          ))
        )}

        {isLoading && (
          <div className="flex items-center gap-2 p-3 bg-slate-100 rounded-lg text-sm text-slate-500">
            <div
              className="w-2 h-2 bg-primary rounded-full"
              style={{ animation: 'pulse 1.5s ease-in-out infinite' }}
            />
            AI is thinking...
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Quick Actions */}
      <QuickActionButtons
        onActionClick={handleQuickAction}
        disabled={isLoading}
      />

      {/* Input Area */}
      <div className="px-4 py-3 border-t border-slate-200 shrink-0">
        <div className="flex gap-2">
          <textarea
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ask for advice..."
            disabled={isLoading}
            rows={2}
            className="flex-1 px-3 py-2 border-2 border-slate-200 rounded-lg text-sm resize-none font-inherit outline-none transition-colors focus:border-primary"
          />
          <button
            onClick={handleSendMessage}
            disabled={!inputText.trim() || isLoading}
            className="px-4 py-2 rounded-lg text-white text-lg flex items-center justify-center transition-all disabled:cursor-not-allowed"
            style={
              inputText.trim() && !isLoading
                ? { background: 'linear-gradient(135deg, #00c6c2, #059669)' }
                : { background: '#cbd5e1' }
            }
          >
            →
          </button>
        </div>
        <p className="text-[10px] text-slate-400 text-center mt-1">
          Press Enter to send • Shift+Enter for new line
        </p>
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
    </div>
  );
}
